import { HeroSection } from '@/components/public/HeroSection';
import { ValuePropositionCards } from '@/components/public/ValuePropositionCards';
import { ProfessionsShowcase } from '@/components/public/ProfessionsShowcase';
import { BannerCarousel } from '@/components/public/BannerCarousel';
import partyRoundTable from '@/images/Party-RoundTable.png';
import bannerImage from '@/images/ReNav-Release-Banner.png';
import '@/styles/animations.css';

/**
 * Feature flag to enable/disable the professions showcase widget
 */
const SHOW_PROFESSIONS_WIDGET = true;

/**
 * Feature card component for highlighting reunion aspects
 */
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay?: number;
}

function FeatureCard({ icon, title, description, delay = 0 }: FeatureCardProps) {
  return (
    <div
      className="card group hover:scale-105 transition-all duration-300 opacity-0 animate-fade-in-up"
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
    >
      <div
        className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
        style={{
          background: 'linear-gradient(135deg, rgba(233, 69, 96, 0.2) 0%, rgba(233, 69, 96, 0.05) 100%)',
          border: '1px solid rgba(233, 69, 96, 0.2)',
        }}
      >
        {icon}
      </div>
      <h3 className="text-xl font-display font-semibold text-white mb-2">{title}</h3>
      <p className="text-white/60 leading-relaxed">{description}</p>
    </div>
  );
}

/**
 * Features/highlights section - Updated with FOMO messaging
 */
function FeaturesSection() {
  const features = [
    {
      icon: (
        <svg className="w-7 h-7 text-romantic-rose" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: 'ReConnect',
      description: '500+ classmates gathering under one roof. Find your lost friends, rekindle old bonds, and build new connections that could last another 30 years.',
    },
    {
      icon: (
        <svg className="w-7 h-7 text-romantic-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      title: 'ReLive',
      description: 'Exclusive photo & video archives from 1996. Curated memory walls, surprise reveals, and nostalgia-packed moments you won\'t find anywhere else.',
    },
    {
      icon: (
        <svg className="w-7 h-7 text-romantic-coral" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
        </svg>
      ),
      title: 'ReCelebrate',
      description: 'Live music, cultural performances, dance, and entertainment throughout the day. A celebration worthy of 30 years of achievements.',
    },
    {
      icon: (
        <svg className="w-7 h-7 text-romantic-peach" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      title: 'ReUnite',
      description: 'Give back together as a batch. Support meaningful causes, create lasting impact, and leave a legacy that represents our class of \'96.',
    },
  ];

  return (
    <section className="py-20 px-4 bg-secondary">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <div className="inline-block px-4 py-2 rounded-full bg-romantic-gold/20 border border-romantic-gold/30 mb-4">
            <span className="text-sm text-romantic-gold font-medium uppercase tracking-wider">
              Don't Miss Out
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Your <span className="text-gradient-romantic">Once-in-a-Lifetime</span> Moment
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto">
            After 30 years, we're not just meeting—we're creating history. This is THE gathering
            everyone will be talking about. Will you be there to be part of it?
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              delay={index * 100}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Event highlight section with key event details - Mega networking emphasis
 */
function EventHighlightSection() {
  return (
    <section className="py-20 px-4 relative overflow-hidden">
      {/* Background gradient with romantic tones */}
      <div
        className="absolute inset-0 opacity-50"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 143, 163, 0.1) 0%, transparent 50%, rgba(255, 215, 0, 0.05) 100%)',
        }}
      />

      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Event info */}
          <div className="opacity-0 animate-fade-in-left" style={{ animationFillMode: 'forwards' }}>
            <div className="inline-block px-4 py-2 rounded-full bg-romantic-rose/20 border border-romantic-rose/30 mb-6">
              <span className="text-sm text-romantic-rose font-medium uppercase tracking-wider">
                The Mega Networking Event of 2026
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">
              June 13th, 2026
            </h2>
            <p className="text-lg text-white/70 mb-4 leading-relaxed">
              The <span className="text-romantic-gold font-semibold">biggest gathering</span> of 1996 batch alumni—500+ professionals
              from across the globe converging for one extraordinary day.
            </p>
            <p className="text-base text-white/60 mb-8 leading-relaxed">
              From grassroots entrepreneurs to global executives, doctors to artists,
              your network awaits. <span className="text-romantic-rose">Don't let another decade pass you by.</span>
            </p>

            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(255, 143, 163, 0.2) 0%, rgba(255, 143, 163, 0.1) 100%)' }}
                >
                  <svg className="w-5 h-5 text-romantic-rose" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-medium">Premium Venue</p>
                  <p className="text-white/60 text-sm">Exclusive location to be announced soon</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 215, 0, 0.1) 100%)' }}
                >
                  <svg className="w-5 h-5 text-romantic-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-medium">Full Day Program</p>
                  <p className="text-white/60 text-sm">8:30 AM onwards • Non-stop celebrations</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, rgba(255, 127, 127, 0.2) 0%, rgba(255, 127, 127, 0.1) 100%)' }}
                >
                  <svg className="w-5 h-5 text-romantic-coral" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-medium">Limited Spots</p>
                  <p className="text-white/60 text-sm">Early registration now open • Secure yours today</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - CTA card with FOMO messaging */}
          <div
            className="opacity-0 animate-fade-in-right"
            style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}
          >
            <div
              className="rounded-2xl p-8 md:p-10 relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(45, 45, 74, 0.9) 0%, rgba(26, 26, 46, 0.9) 100%)',
                border: '1px solid rgba(255, 143, 163, 0.3)',
                boxShadow: '0 0 60px rgba(255, 143, 163, 0.15)',
              }}
            >
              {/* Sparkle decoration */}
              <div
                className="absolute top-4 right-4 w-2 h-2 rounded-full animate-twinkle"
                style={{ background: 'rgba(255, 215, 0, 0.8)' }}
                aria-hidden="true"
              />

              {/* Urgency badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-romantic-gold/20 border border-romantic-gold/30 mb-4">
                <span className="w-2 h-2 rounded-full bg-romantic-gold animate-pulse" aria-hidden="true" />
                <span className="text-xs text-romantic-gold font-medium uppercase tracking-wider">
                  Filling Up Fast
                </span>
              </div>

              <h3 className="text-2xl font-display font-bold text-white mb-4">
                Secure Your Spot Today
              </h3>
              <p className="text-white/60 mb-6">
                Don't wait until it's too late. Get your exclusive reunion ticket with unique QR code
                and join the <span className="text-romantic-rose">mega networking event</span> of the decade.
              </p>


              {/* Benefits list */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-white/50">
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4 text-romantic-gold" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Quick registration</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4 text-romantic-gold" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Instant confirmation</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4 text-romantic-gold" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>QR ticket</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Quick info section with three key points - Romantic colors
 */
function QuickInfoSection() {
  return (
    <section className="py-16 px-4 bg-secondary-dark relative overflow-hidden">
      {/* Subtle sparkle accents */}
      <div
        className="absolute top-8 left-1/4 w-1.5 h-1.5 rounded-full animate-twinkle"
        style={{ background: 'rgba(255, 215, 0, 0.6)' }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-8 right-1/4 w-1 h-1 rounded-full animate-sparkle"
        style={{ background: 'rgba(255, 143, 163, 0.6)', animationDelay: '500ms' }}
        aria-hidden="true"
      />

      <div className="container mx-auto max-w-4xl relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: '0ms', animationFillMode: 'forwards' }}>
            <div className="text-5xl font-display font-bold text-gradient-romantic mb-2">1996</div>
            <p className="text-white/60">The Year We Graduated</p>
          </div>
          <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
            <div className="text-5xl font-display font-bold text-romantic-gold mb-2">30</div>
            <p className="text-white/60">Years of Success Stories</p>
          </div>
          <div className="opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
            <div className="text-5xl font-display font-bold text-romantic-rose mb-2">500+</div>
            <p className="text-white/60">Alumni Expected</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Call to action section at the bottom - Strong FOMO messaging
 */
function FinalCTASection() {
  return (
    <section className="py-20 px-4 relative overflow-hidden">
      {/* Background pattern with romantic tones */}
      <div className="absolute inset-0 opacity-30">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255, 143, 163, 0.2) 0%, transparent 50%),
                             radial-gradient(circle at 80% 70%, rgba(255, 215, 0, 0.15) 0%, transparent 50%)`,
          }}
        />
      </div>

      {/* Floating sparkles */}
      <div
        className="absolute top-1/4 left-1/4 w-2 h-2 rounded-full animate-twinkle"
        style={{ background: 'rgba(255, 215, 0, 0.7)' }}
        aria-hidden="true"
      />
      <div
        className="absolute bottom-1/3 right-1/3 w-1.5 h-1.5 rounded-full animate-sparkle"
        style={{ background: 'rgba(255, 143, 163, 0.7)', animationDelay: '1s' }}
        aria-hidden="true"
      />

      <div className="container mx-auto max-w-3xl relative z-10 text-center">
        {/* Urgency indicator */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-romantic-rose/20 border border-romantic-rose/30 mb-6">
          <span className="w-2 h-2 rounded-full bg-romantic-rose animate-pulse" aria-hidden="true" />
          <span className="text-sm text-romantic-rose font-medium">
            Once in 30 years — Don't let another decade pass
          </span>
        </div>

        <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-6">
          Your Network Is <span className="text-gradient-romantic">Waiting</span>
        </h2>
        <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
          500+ classmates. Global professionals. Life-changing connections.
          This is THE networking event of 2026 that everyone will be talking about.
          <span className="block mt-2 text-romantic-gold font-medium">Will they be talking about seeing you there?</span>
        </p>
        <p className="mt-6 text-sm text-white/40">
          Quick registration • Instant confirmation • Limited spots
        </p>
      </div>
    </section>
  );
}

/**
 * HomePage Component
 *
 * The main public landing page for the Re:Nav-96 reunion website.
 * Features a vibrant hero section with countdown timer, feature highlights,
 * event details, and call-to-action sections.
 *
 * Sections:
 * 1. HeroSection - Animated hero with countdown, sparkles, and tagline carousel
 * 2. ValuePropositionCards - FOMO-inducing benefit cards
 * 3. FeaturesSection - Key highlights of the reunion (Re:Nav themed)
 * 4. Party Round Table image
 * 5. EventHighlightSection - Event date, time, and registration CTA
 * 6. ProfessionsShowcase - Alumni professions grid (toggleable)
 * 7. BannerCarousel - Classroom memories carousel
 * 8. EventSchedule - Timeline of the day's events
 * 9. Banner image - ReNav Release Banner
 * 10. ActivitiesSection - Fun activities and things to do
 * 9. CharitableCauses - Charitable causes we support
 * 10. QuickInfoSection - Quick stats with romantic styling
 * 11. FinalCTASection - Bottom call-to-action with FOMO messaging
 *
 * @example
 * ```tsx
 * <HomePage />
 * ```
 */
export function HomePage() {
  return (
    <div className="min-h-screen">
      {/* Hero section with countdown and sparkle effects */}
      <HeroSection />

      {/* Value proposition cards - FOMO messaging */}
      <ValuePropositionCards />

      {/* Features section - Re:Nav themed */}
      <FeaturesSection />

      {/* Party round table image */}
      <section className="py-8 px-4 bg-secondary relative overflow-hidden">
        <div className="container mx-auto max-w-6xl flex justify-center">
          <div
            className="w-1/2 rounded-2xl overflow-hidden opacity-0 animate-fade-in-up"
            style={{
              animationFillMode: 'forwards',
              border: '1px solid rgba(233, 69, 96, 0.15)',
              boxShadow: '0 0 40px rgba(233, 69, 96, 0.1)',
            }}
          >
            <img src={partyRoundTable} alt="Party Round Table" className="w-full h-auto block" />
          </div>
        </div>
      </section>

      {/* Event highlight section - Mega networking emphasis */}
      <EventHighlightSection />

      {/* Alumni professions showcase (with feature flag) */}
      <ProfessionsShowcase enabled={SHOW_PROFESSIONS_WIDGET} />

      {/* Banner carousel - classroom memories */}
      <BannerCarousel />

      {/* Banner image */}
      <section className="py-8 px-4 bg-secondary relative overflow-hidden">
        <div className="container mx-auto max-w-6xl flex justify-center">
          <div
            className="w-1/2 rounded-2xl overflow-hidden opacity-0 animate-fade-in-up"
            style={{
              animationFillMode: 'forwards',
              border: '1px solid rgba(233, 69, 96, 0.15)',
              boxShadow: '0 0 40px rgba(233, 69, 96, 0.1)',
            }}
          >
            <img src={bannerImage} alt="ReNav Release Banner" className="w-full h-auto block" />
          </div>
        </div>
      </section>

      {/* Quick info section - Romantic styling */}
      <QuickInfoSection />

      {/* Final CTA section - Strong FOMO messaging */}
      <FinalCTASection />
    </div>
  );
}

export default HomePage;
